import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { cssVariables } from '@env/css-vars';
import { catchError, first, map as rxjsMap } from 'rxjs/operators';
import { map, keys, values } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Excel, ExcelService } from '@app/shared/services/api/excel.service';
import { Subject, of } from 'rxjs';
import { saveFile } from '@app/shared/utility';
import { AuthService } from '@app/shared/services/auth.service';
import { NotificationDialogComponent } from '../../../shared/dialog/notification-dialog/notification-dialog.component';
import { DashoardProperties } from '@app/shared/models/AppInsights';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import moment from 'moment';
import { StreetlightApiService } from '@app/shared/services/api/streetlight.service';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { Luminaire } from '@app/shared/models/energiekarte/luminaire';

@Component({
  selector: 'app-streetlights-widget-dialog',
  templateUrl: './streetlights-widget-dialog.component.html',
  styleUrls: ['./streetlights-widget-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StreetlightsWidgetDialogComponent implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  hideClose = false;
  loading = false;

  constructor(
    public authService: AuthService,
    private overlayLoaderService: OverlayLoaderService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private streetlightsService: StreetlightApiService,
    private translate: TranslateService,
    private matDialog: MatDialog,
    private excelService: ExcelService,
    private matDialogRef: MatDialogRef<StreetlightsWidgetDialogComponent>,
    private readonly localNumberPipe: LocalNumberPipe,
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  formatDataToExport(lamp: Luminaire): Record<string, string | number> {
    return {
      Gemeinde: lamp.municipality,
      Gemeindeteil: lamp.district,
      Straße: lamp.street,
      Standort: lamp.locationInfo,
      Leuchtstellennummer: lamp.streetLightNumber,
      Leuchtenform: lamp.luminaireForm,
      Leuchtentyp: lamp.actualLightType,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Anzahl Leuchten': lamp.numberOfLuminaires,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Anzahl Leuchtmittel': lamp.numberOfLamps,
      Leuchtmitteltyp: lamp.lampType,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Leistung je Leuchtmittel [W]': this.localNumberPipe.transform(lamp.powerPerLamp),
    };
  }

  generateExcel(csvData: Array<Record<string, string | number>>) {
    const date = moment().format('YYMMDD');
    const excelData: Excel = {
      fileName: `${this.userMunicipalitiesService.currentMunicipality.name}_Leuchtstellenliste_${date}.xlsx`,
      sheetName: `Leuchtstellen`,
      headers: keys(csvData[0]),
      data: map(csvData, values),
      protectedColumns: [],
      templateCode: '',
      headerBgColor: '',
      secretPhrase: '',
    };

    this.excelService
      .generateExcel(excelData)
      .pipe(
        catchError(() => {
          this.overlayLoaderService.isLoading = false;
          return of(null);
        }),
        first(),
      )
      .subscribe((xlsx: any) => {
        const blob = new Blob([xlsx], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveFile(blob, excelData.fileName);
        this.overlayLoaderService.isLoading = false;
      });
  }

  downloadStreetlights(link: string) {
    this.appInsightsServiceDirective.logEvent<DashoardProperties>(ApplicationInsightEvent.clickDashboard, { Link: link });

    this.matDialogRef.close(false);
    this.overlayLoaderService.isLoading = true;
    const filters = { includeNoCoordinates: true, includeNoCategory: true };
    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
    this.streetlightsService
      .getLuminairesByMunicipalityKey(municipalityKey, filters)
      .pipe(
        catchError(() => {
          this.overlayLoaderService.isLoading = false;
          return of([]);
        }),
        rxjsMap((lamps: Luminaire[]) => (lamps.length > 0 ? lamps.map((lamp: Luminaire) => this.formatDataToExport(lamp)) : [])),
        first(),
      )
      .subscribe(
        (csvData: Array<Record<string, string | number>>) => {
          if (csvData.length <= 0) {
            this.matDialog.open(NotificationDialogComponent, {
              data: {
                descriptionText: this.translate.instant('module.energyMap.noStretlightsInMunicipalityMessage'),
                descriptionFaClass: 'fa-exclamation-triangle fa-2x',
                descriptionFaColor: cssVariables['--ep-error-color'],
              },
              panelClass: ['-md'],
            });
            this.overlayLoaderService.isLoading = false;
            return;
          }

          this.generateExcel(csvData);
          this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.brennstellenliste, {
            municipalityName: this.userMunicipalitiesService.currentMunicipality.name,
            role: this.authService.currentUser.roleName,
          });
        },
        () => {
          this.overlayLoaderService.isLoading = false;
        },
      );
  }

  closeModal(link: string) {
    this.appInsightsServiceDirective.logEvent<DashoardProperties>(ApplicationInsightEvent.clickDashboard, { Link: link });

    this.matDialogRef.close(false);
  }
}
